import { useEffect, useRef, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useDropdownContext } from '../../context/DropdownContext';

interface Position {
  top: number;
  left: number;
}

interface PortalDropdownMenuProps {
  dropdownId: string;
  triggerRef: React.RefObject<HTMLElement>;
  children: ReactNode;
  align?: 'left' | 'right';
  width?: number;
}

export function PortalDropdownMenu({
  dropdownId,
  triggerRef,
  children,
  align = 'right',
  width = 224, // w-56 = 14rem = 224px
}: PortalDropdownMenuProps) {
  const { isOpen, closeDropdown } = useDropdownContext();
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });

  const open = isOpen(dropdownId);

  // Calculate position when menu opens
  useEffect(() => {
    if (!open || !triggerRef.current) return;

    const calculatePosition = () => {
      const triggerRect = triggerRef.current!.getBoundingClientRect();
      const menuHeight = menuRef.current?.offsetHeight || 300;
      const gap = 4;

      let top = triggerRect.bottom + gap;
      let left = align === 'right'
        ? triggerRect.right - width
        : triggerRect.left;

      // Flip above if not enough space below
      if (top + menuHeight > window.innerHeight) {
        top = triggerRect.top - menuHeight - gap;
      }

      // Prevent horizontal overflow
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));

      setPosition({ top, left });
    };

    // Calculate immediately
    calculatePosition();

    // Recalculate after a frame to get actual menu height
    requestAnimationFrame(calculatePosition);
  }, [open, triggerRef, align, width]);

  // Handle click outside
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside menu or trigger
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;

      closeDropdown();
    };

    // Use mousedown for immediate response
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, closeDropdown, triggerRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: width,
      }}
      className="z-[9999] rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5"
    >
      {children}
    </div>,
    document.body
  );
}
